import type { LoginData, RegisterData, Project, Scan, Finding, GlobalFindingsResponse, DashboardData, ExecutiveSummary, PciReportData, BenchmarkData, RetestResult, SharedReportLink, SharedReportData, Team, TeamMember, TeamInvite, PendingInvite, FreeCheckResult, AdminUser, AdminUserDetail, AdminStats, FreeScanSummary, FreeScanDetail, FreeScanStats, GithubInstallation, GithubRepo, GithubAppStatus, GithubComparison, SsoConfig, SsoConfigInput, SsoDomain, TrustCenter, TrustCenterPublic, TrustControl, TrustSubprocessor, TrustDocument, TrustCertification, TrustCertificationFramework, TrustAccessRequest, TrustAccessGrant, TrustCenterStats, McpClient, McpAccessLogPage, McpInfo } from './types';
import { t, getLang } from './i18n/context';
import { getResolvedTheme } from './theme/context';

const API_BASE = '/api/v1';

const LOGGED_IN_KEY = 'condor_logged_in';
const ROLE_KEY = 'condor_role';

/**
 * ¿Este status dice que el servidor RECHAZÓ la credencial?
 *
 * Sólo 401 (no autenticado) y 403 (prohibido) son veredictos sobre el refresh
 * token. Cualquier 5xx —incluido el 530 de Cloudflare, «no alcancé el origen»—
 * significa que el token ni siquiera se evaluó, y un 429 es cuota, no identidad.
 * Tratar esos como rechazo cierra la sesión por una caída pasajera del backend.
 */
function isCredentialRejection(status: number): boolean {
  return status === 401 || status === 403;
}

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

class ApiClient {
  private authenticated = localStorage.getItem(LOGGED_IN_KEY) === '1';
  private loggingOut = false;
  private refreshPromise: Promise<boolean> | null = null;
  private onLogoutCallback: ((reason: 'auto' | 'explicit') => void) | null = null;

  /**
   * Register a callback invoked whenever the client logs out. The
   * callback receives the reason: "auto" for 401-triggered logouts
   * (session expired, refresh failed) and "explicit" for user-initiated
   * logouts. Consumers typically show a toast only on "auto".
   */
  onAutoLogout(cb: (reason: 'auto' | 'explicit') => void) {
    this.onLogoutCallback = cb;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    // Skip requests if we're in the process of logging out
    if (this.loggingOut && !path.startsWith('/auth/')) {
      throw new ApiError(401, t('errors.session_closed'));
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    let res: Response;
    try {
      res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
    } catch {
      throw new ApiError(0, t('errors.connection_error'));
    }

    if (res.status === 401 && !path.startsWith('/auth/')) {
      // Try refresh before logging out
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        // Retry the original request — cookies are already updated
        try {
          res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
        } catch {
          throw new ApiError(0, t('errors.connection_error'));
        }
        if (res.ok) {
          return res.json();
        }
      }
      // El refresh no prosperó, pero eso no siempre significa sesión muerta:
      // si cayó por transporte (5xx / red), la credencial sigue siendo válida y
      // el próximo intento puede funcionar. Cerrar sesión sólo cuando el
      // servidor la rechazó de verdad — `authenticated` ya refleja ese veredicto.
      if (!this.authenticated) {
        this.logout({ reason: 'auto' });
      }
      throw new ApiError(401, t('errors.session_expired'));
    }

    if (!res.ok) {
      throw await this.errorFromResponse(res);
    }

    if (res.status === 204) return undefined as T;

    try {
      return await res.json() as T;
    } catch {
      throw new ApiError(res.status, t('errors.invalid_response'));
    }
  }

  /** Build an ApiError from a non-OK response, extracting FastAPI-style detail. */
  private async errorFromResponse(res: Response): Promise<ApiError> {
    let detail = t('errors.server_error', { status: res.status });
    try {
      const body = await res.json();
      const raw = body.detail ?? body.message;
      if (typeof raw === 'string') {
        detail = raw;
      } else if (Array.isArray(raw)) {
        // FastAPI validation errors come back as [{loc, msg, type, ...}]
        detail = raw
          .map((e) => (typeof e === 'string' ? e : e?.msg ?? JSON.stringify(e)))
          .join('; ');
      } else if (raw && typeof raw === 'object') {
        detail = (raw as { msg?: string }).msg ?? JSON.stringify(raw);
      }
    } catch {
      // Response body was not JSON — use default message
    }
    if (res.status === 403 && (detail.toLowerCase().includes('limit') || detail.toLowerCase().includes('upgrade'))) {
      return new ApiError(res.status, `${detail} — ${t('errors.quota_exceeded')}`);
    }
    return new ApiError(res.status, detail);
  }

  private async tryRefresh(): Promise<boolean> {
    // Deduplicate concurrent refresh calls
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) {
          // Sólo un rechazo explícito de la credencial invalida la sesión.
          // Un 5xx (502/530 de Cloudflare cuando el origen no responde) significa
          // «no se pudo evaluar el refresh token», no «el refresh token es malo»:
          // descartar la sesión ahí desloguea al usuario por un parpadeo de red.
          if (isCredentialRejection(res.status)) {
            this.authenticated = false;
            localStorage.removeItem(LOGGED_IN_KEY);
            localStorage.removeItem(ROLE_KEY);
          }
          return false;
        }
        return true;
      } catch {
        // La petición ni siquiera llegó a la red: no dice nada de la credencial.
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Clear client-side auth state and POST /auth/logout.
   *
   * @param opts.reason  "auto" when a 401 forced us out (shows the
   *   "Session expired" toast via the callback). "explicit" or omitted
   *   means the user clicked Logout on purpose — no error-toned toast,
   *   no surprise. The callback still runs so routes redirect, but it
   *   can branch on the reason if the caller registered it that way.
   */
  logout(opts?: { reason?: 'auto' | 'explicit' }) {
    // Idempotent: concurrent 401s (multiple React Query pollers hitting a
    // dead session simultaneously) must not each fire the logout callback —
    // otherwise the toast + navigate stack up one per polling loop.
    if (this.loggingOut) return;
    this.loggingOut = true;
    this.authenticated = false;
    localStorage.removeItem(LOGGED_IN_KEY);
    localStorage.removeItem(ROLE_KEY);
    // Tell the API to clear cookies
    fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    // Notify listener (e.g. navigate to /login). Default reason is
    // 'explicit' so UI callbacks that branch on this don't fire
    // error toasts for user-initiated logouts.
    this.onLogoutCallback?.(opts?.reason ?? 'explicit');
    // Reset flag after a tick so future login requests work
    setTimeout(() => { this.loggingOut = false; }, 0);
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  async login(data: LoginData): Promise<{ mfa_required?: boolean }> {
    const json = await this.request<{ mfa_required?: boolean; platform_role?: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (json.mfa_required) {
      return json;
    }
    this.authenticated = true;
    localStorage.setItem(LOGGED_IN_KEY, '1');
    if (json.platform_role) {
      localStorage.setItem(ROLE_KEY, json.platform_role);
    }
    return json;
  }

  /** Home-realm discovery: dado un email, dice si su org usa SSO y a dónde ir. */
  async discoverSso(email: string): Promise<{ sso: boolean; sso_only?: boolean; login_url?: string }> {
    return this.request('/auth/sso/discover', { method: 'POST', body: JSON.stringify({ email }) });
  }

  /** Tras volver del IdP (cookie de sesión ya seteada por el backend), adopta la
   *  sesión: confirma vía /me y marca al cliente como autenticado. */
  async adoptSsoSession(): Promise<boolean> {
    try {
      const me = await this.getProfile();
      this.authenticated = true;
      localStorage.setItem(LOGGED_IN_KEY, '1');
      localStorage.setItem(ROLE_KEY, me.platform_role);
      localStorage.setItem('condor_user_name', me.name);
      return true;
    } catch {
      return false;
    }
  }

  async loginWithMfa(email: string, password: string, mfaCode: string): Promise<void> {
    const json = await this.request<{ platform_role?: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      headers: { 'X-MFA-Code': mfaCode },
    });
    this.authenticated = true;
    localStorage.setItem(LOGGED_IN_KEY, '1');
    if (json.platform_role) {
      localStorage.setItem(ROLE_KEY, json.platform_role);
    }
  }

  async register(data: RegisterData) {
    return this.request('/auth/register', { method: 'POST', body: JSON.stringify(data) });
  }

  async requestPasswordReset(email: string) {
    return this.request('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async confirmPasswordReset(token: string, new_password: string) {
    return this.request('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, new_password }),
    });
  }

  async verifyEmail(token: string) {
    return this.request(`/auth/verify-email?token=${encodeURIComponent(token)}`, {
      method: 'POST',
    });
  }

  async changePassword(current_password: string, new_password: string) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    });
  }

  async getProfile() {
    return this.request<{
      id: string;
      name: string;
      email: string;
      company: string | null;
      phone: string | null;
      github_login: string | null;
      platform_role: string;
      is_active: boolean;
      email_verified: boolean;
      created_at: string;
      updated_at: string;
    }>('/me/');
  }

  async updateProfile(data: {
    name?: string;
    company?: string;
    phone?: string;
    github_login?: string | null;
  }) {
    return this.request('/me/', { method: 'PATCH', body: JSON.stringify(data) });
  }

  async getProjects(): Promise<Project[]> {
    return this.request<Project[]>('/projects/');
  }

  async createProject(data: { name: string; target_url?: string; target_type?: string; business_type?: string }): Promise<Project> {
    return this.request<Project>('/projects/', { method: 'POST', body: JSON.stringify(data) });
  }

  async getProject(id: string): Promise<Project> {
    return this.request<Project>(`/projects/${id}`);
  }

  async deleteProject(id: string): Promise<void> {
    await this.request<void>(`/projects/${id}`, { method: 'DELETE' });
  }

  async getScans(projectId: string): Promise<Scan[]> {
    return this.request<Scan[]>(`/projects/${projectId}/scans/`);
  }

  async createScan(projectId: string, data: { scan_type: string; mode?: string; compliance_mode?: string }): Promise<Scan> {
    return this.request<Scan>(`/projects/${projectId}/scans/`, { method: 'POST', body: JSON.stringify(data) });
  }

  async getFindings(projectId: string, scanId: string, params?: {
    severity?: string;
    status?: string;
    confidence?: string;
    search?: string;
    source?: string;
    // Comma-separated AI verdict filter. `__none__` matches unclassified rows
    // (no ai_verdict). Values AND with the other filters server-side.
    ai_verdict?: string;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<{ findings: Finding[]; total: number; page: number; page_size: number; pages: number; severity_summary: Record<string, number>; source_summary?: Record<string, number> }> {
    const searchParams = new URLSearchParams();
    if (params?.severity) searchParams.set('severity', params.severity);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.confidence) searchParams.set('confidence', params.confidence);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.source) searchParams.set('source', params.source);
    if (params?.ai_verdict) searchParams.set('ai_verdict', params.ai_verdict);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params?.sort_order) searchParams.set('sort_order', params.sort_order);
    const qs = searchParams.toString();
    return this.request(`/projects/${projectId}/scans/${scanId}/findings${qs ? '?' + qs : ''}`);
  }

  /** Dashboard stats. `days` scopes the findings trend window (7|15|30|90,
   * agreed contract with condor-api). Older APIs ignore the param and keep
   * returning the 30-day series — the caller slices client-side. */
  async getDashboard(days: number = 30): Promise<DashboardData> {
    return this.request<DashboardData>(`/dashboard/?days=${days}`);
  }

  /** Cross-project findings list — same selection logic as the dashboard
   * counters, so an unfiltered request returns exactly `total_findings` rows.
   * `status`: omitted = active only; also `accepted` | `false_positive` | `all`. */
  async getGlobalFindings(params?: {
    severity?: string; // CSV: critical,high,...
    project_id?: string;
    status?: string;
    source?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<GlobalFindingsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.severity) searchParams.set('severity', params.severity);
    if (params?.project_id) searchParams.set('project_id', params.project_id);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.source) searchParams.set('source', params.source);
    if (params?.q) searchParams.set('q', params.q);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return this.request<GlobalFindingsResponse>(`/findings/${qs ? '?' + qs : ''}`);
  }

  // ── GitHub integration ──

  async startGithubInstall(): Promise<{ install_url: string }> {
    return this.request<{ install_url: string }>('/integrations/github/install', { method: 'POST' });
  }

  async listGithubInstallations(): Promise<GithubInstallation[]> {
    return this.request<GithubInstallation[]>('/integrations/github/');
  }

  async listGithubRepos(installationId: number): Promise<GithubRepo[]> {
    return this.request<GithubRepo[]>(`/integrations/github/${installationId}/repos`);
  }

  async forgetGithubInstallation(installationId: number): Promise<void> {
    await this.request<void>(`/integrations/github/${installationId}`, { method: 'DELETE' });
  }

  async getGithubAppStatus(): Promise<GithubAppStatus> {
    return this.request<GithubAppStatus>('/integrations/github/app/status');
  }

  async getGithubAppManifest(): Promise<{ action_url: string; manifest: Record<string, unknown>; callback: string }> {
    return this.request<{ action_url: string; manifest: Record<string, unknown>; callback: string }>('/integrations/github/app/manifest-payload');
  }

  async cancelScan(projectId: string, scanId: string): Promise<Scan> {
    return this.request<Scan>(`/projects/${projectId}/scans/${scanId}/cancel`, {
      method: 'POST',
    });
  }

  async deleteScan(projectId: string, scanId: string): Promise<void> {
    await this.request<void>(`/projects/${projectId}/scans/${scanId}`, {
      method: 'DELETE',
    });
  }

  async getScan(projectId: string, scanId: string): Promise<Scan> {
    return this.request<Scan>(`/projects/${projectId}/scans/${scanId}`);
  }

  async updateFindingStatus(projectId: string, scanId: string, findingId: string, status: string): Promise<Finding> {
    return this.request<Finding>(`/projects/${projectId}/scans/${scanId}/findings/${findingId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async getFinding(projectId: string, scanId: string, findingId: string): Promise<Finding> {
    return this.request<Finding>(`/projects/${projectId}/scans/${scanId}/findings/${findingId}`);
  }

  async requestRetest(projectId: string, scanId: string, findingIds?: string[]): Promise<RetestResult> {
    return this.request<RetestResult>(`/projects/${projectId}/scans/${scanId}/retest`, {
      method: 'POST',
      body: JSON.stringify({ finding_ids: findingIds }),
    });
  }

  async getExecutiveSummary(projectId: string, lang: string): Promise<ExecutiveSummary> {
    return this.request<ExecutiveSummary>(`/projects/${projectId}/executive-summary?lang=${lang}`);
  }

  async getPciReport(projectId: string, scanId: string, lang: string): Promise<PciReportData> {
    return this.request<PciReportData>(`/projects/${projectId}/scans/${scanId}/pci-report?lang=${lang}`);
  }

  async getBenchmark(projectId: string, lang: string): Promise<BenchmarkData> {
    return this.request<BenchmarkData>(`/projects/${projectId}/benchmark?lang=${lang}`);
  }

  async getGithubComparison(projectId: string): Promise<GithubComparison> {
    return this.request<GithubComparison>(`/projects/${projectId}/github-comparison`);
  }

  async updateProject(projectId: string, data: {
    name?: string;
    target_url?: string;
    business_type?: string;
    compliance_mode?: string;
    github_deep_scan?: boolean;
    github_auto_deep_scan?: boolean;
    github_auto_scan_mode?: 'light' | 'standard' | 'aggressive' | null;
    github_ref?: string | null;
    github_exclude_paths?: string[];
    github_baseline?: string[];
    pr_gate?: 'off' | 'critical' | 'high' | 'medium';
    scan_schedule?: 'off' | 'daily' | 'weekly';
    scan_schedule_day?: number;
    scan_schedule_hour?: number;
    notifications_enabled?: boolean;
    notify_email?: boolean;
    notify_webhook?: boolean;
    notification_email?: string | null;
    webhook_url?: string | null;
  }): Promise<Project> {
    return this.request<Project>(`/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getScanAnalysis(projectId: string, scanId: string): Promise<{
    stats: Record<string, unknown>;
    tech_info: Record<string, string | null>;
    grouped_findings: Array<{
      title: string;
      severity: string;
      cwe_id: string | null;
      instance_count: number;
      confidence: string;
      compliance?: Record<string, unknown>;
    }>;
  }> {
    return this.request(`/projects/${projectId}/scans/${scanId}/analysis`);
  }

  // Billing
  async getPlans() {
    return this.request<unknown[]>('/billing/plans');
  }

  async getSubscription() {
    return this.request<{
      plan: string;
      status: string;
      scans_used: number;
      scans_limit: number;
      projects_used: number;
      projects_limit: number;
      trial_ends_at?: string;
    }>('/billing/subscription');
  }

  async upgradePlan(plan: string) {
    return this.request('/billing/upgrade', { method: 'POST', body: JSON.stringify({ plan }) });
  }

  async getUsageHistory() {
    return this.request<Array<{ date: string; scans: number; projects: number; action: string }>>('/billing/usage');
  }

  async createCheckoutSession(plan: string) {
    return this.request<{ url: string }>('/billing/checkout', { method: 'POST', body: JSON.stringify({ plan }) });
  }

  async getCustomerPortalUrl() {
    return this.request<{ url: string }>('/billing/portal', { method: 'POST' });
  }

  async getBillingHistory() {
    return this.request<Array<{ id: string; date: string; amount: string; description: string; status: string; invoice_url?: string }>>('/billing/history');
  }

  // MFA
  async getMfaStatus() {
    return this.request<{ enabled: boolean; has_backup_codes: boolean }>('/security/mfa/status');
  }

  async setupMfa() {
    return this.request<{ secret: string; otpauth_uri: string; qr_data: string }>('/security/mfa/setup', { method: 'POST' });
  }

  async confirmMfa(code: string) {
    return this.request<{ enabled: boolean; backup_codes: string[] }>('/security/mfa/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async disableMfa(code: string) {
    return this.request('/security/mfa/disable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async regenerateBackupCodes(code: string) {
    return this.request<{ backup_codes: string[] }>('/security/mfa/backup-codes', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  // Domain Verification
  async getDomainVerification(projectId: string) {
    return this.request<{
      verified: boolean;
      domain: string;
      token: string;
      methods: Array<{ method: string; verified: boolean; instructions: string }>;
      verification_source?: 'github_app' | 'team_dns' | 'challenge';
      challenge_required?: boolean;
    }>(`/security/domain-verification/${projectId}`);
  }

  async verifyDomain(projectId: string, method: string) {
    return this.request<{ verified: boolean; message: string }>(`/security/domain-verification/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({ method }),
    });
  }

  // Audit Logs
  async getAuditLogs(params?: { action?: string; limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.action) searchParams.set('action', params.action);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    return this.request<{
      logs: Array<{
        id: string;
        action: string;
        timestamp: string;
        ip_address: string;
        success: boolean;
        details?: string;
      }>;
      total: number;
    }>(`/security/audit-logs${qs ? '?' + qs : ''}`);
  }

  // Storage
  async getStorageConfig() {
    return this.request<{
      provider: string;
      bucket: string;
      region: string;
      prefix: string;
      configured: boolean;
    }>('/settings/storage');
  }

  async updateStorageConfig(data: {
    provider: string;
    bucket: string;
    region: string;
    access_key: string;
    secret_key: string;
    prefix?: string;
  }) {
    return this.request('/settings/storage', { method: 'PUT', body: JSON.stringify(data) });
  }

  async testStorageConnection() {
    return this.request<{ success: boolean; message: string }>('/settings/storage/test', { method: 'POST' });
  }

  async getStorageUsage() {
    return this.request<{
      total_bytes: number;
      report_count: number;
      reports: Array<{
        id: string;
        project_name: string;
        scan_id: string;
        filename: string;
        size_bytes: number;
        created_at: string;
      }>;
    }>('/settings/storage/usage');
  }

  async downloadStoredReport(reportId: string): Promise<void> {
    const res = await fetch(`${API_BASE}/settings/storage/reports/${reportId}/download`, {
      credentials: 'include',
    });
    if (!res.ok) throw new ApiError(res.status, t('errors.download_error', { status: res.status }));
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="(.+)"/);
    a.download = match ? match[1] : `report-${reportId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // Sessions
  async getSessions() {
    return this.request<{
      sessions: Array<{
        id: string;
        ip_address: string;
        user_agent: string;
        created_at: string;
        last_active: string;
        is_current: boolean;
      }>;
    }>('/security/sessions');
  }

  async revokeSession(sessionId: string) {
    return this.request(`/security/sessions/${sessionId}`, { method: 'DELETE' });
  }

  async revokeAllOtherSessions() {
    return this.request('/security/sessions/revoke-others', { method: 'POST' });
  }

  // Teams
  async getTeams(): Promise<Team[]> {
    return this.request<Team[]>('/teams/');
  }

  async createTeam(data: { name: string }): Promise<Team> {
    return this.request<Team>('/teams/', { method: 'POST', body: JSON.stringify(data) });
  }

  async deleteTeam(teamId: string): Promise<void> {
    await this.request<void>(`/teams/${teamId}`, { method: 'DELETE' });
  }

  async getTeamMembers(teamId: string): Promise<TeamMember[]> {
    return this.request<TeamMember[]>(`/teams/${teamId}/members`);
  }

  async inviteTeamMember(
    teamId: string,
    data: { email?: string; github_login?: string; role: string },
  ): Promise<TeamInvite> {
    return this.request<TeamInvite>(`/teams/${teamId}/invites`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPendingInvites(): Promise<PendingInvite[]> {
    return this.request<PendingInvite[]>('/teams/invites/pending');
  }

  // Inbox actions for the logged-in user's own pending invites (cross-team).
  // Id-based, unlike the token magic-link accept, so the inbox can act on the
  // invites returned by getPendingInvites() (which never expose the token).
  async acceptInvite(inviteId: string): Promise<{ team_id?: string; role?: string; message?: string }> {
    return this.request(`/teams/invites/${inviteId}/accept`, { method: 'POST' });
  }

  async declineInvite(inviteId: string): Promise<void> {
    await this.request<void>(`/teams/invites/${inviteId}/decline`, { method: 'POST' });
  }

  async getTeamInvites(teamId: string): Promise<TeamInvite[]> {
    return this.request<TeamInvite[]>(`/teams/${teamId}/invites`);
  }

  async cancelTeamInvite(teamId: string, inviteId: string): Promise<void> {
    await this.request<void>(`/teams/${teamId}/invites/${inviteId}`, { method: 'DELETE' });
  }

  async updateMemberRole(teamId: string, memberId: string, role: string): Promise<TeamMember> {
    return this.request<TeamMember>(`/teams/${teamId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async removeTeamMember(teamId: string, memberId: string): Promise<void> {
    await this.request<void>(`/teams/${teamId}/members/${memberId}`, { method: 'DELETE' });
  }

  async switchTeam(teamId: string): Promise<void> {
    await this.request<unknown>(`/teams/${teamId}/switch`, { method: 'POST' });
    // Cookies are updated automatically by the API response
  }

  // ── SSO ──
  async getSso(teamId: string): Promise<SsoConfig | null> {
    return this.request<SsoConfig | null>(`/teams/${teamId}/sso`);
  }
  async putSso(teamId: string, cfg: SsoConfigInput): Promise<SsoConfig> {
    return this.request<SsoConfig>(`/teams/${teamId}/sso`, { method: 'PUT', body: JSON.stringify(cfg) });
  }
  async deleteSso(teamId: string): Promise<void> {
    await this.request<void>(`/teams/${teamId}/sso`, { method: 'DELETE' });
  }
  async listSsoDomains(teamId: string): Promise<SsoDomain[]> {
    return this.request<SsoDomain[]>(`/teams/${teamId}/domains`);
  }
  async addSsoDomain(teamId: string, domain: string): Promise<SsoDomain> {
    return this.request<SsoDomain>(`/teams/${teamId}/domains`, { method: 'POST', body: JSON.stringify({ domain }) });
  }
  async verifySsoDomain(teamId: string, domainId: string): Promise<SsoDomain> {
    return this.request<SsoDomain>(`/teams/${teamId}/domains/${domainId}/verify`, { method: 'POST' });
  }
  async deleteSsoDomain(teamId: string, domainId: string): Promise<void> {
    await this.request<void>(`/teams/${teamId}/domains/${domainId}`, { method: 'DELETE' });
  }
  async generateScimToken(teamId: string): Promise<{ token: string; scim_url: string }> {
    return this.request<{ token: string; scim_url: string }>(`/teams/${teamId}/sso/scim-token`, { method: 'POST' });
  }

  // Shared Report Links
  async createShareLink(projectId: string, scanId: string): Promise<SharedReportLink> {
    return this.request<SharedReportLink>(`/projects/${projectId}/scans/${scanId}/share`, { method: 'POST' });
  }

  async getShareLink(projectId: string, scanId: string): Promise<SharedReportLink | null> {
    try {
      return await this.request<SharedReportLink>(`/projects/${projectId}/scans/${scanId}/share`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }

  async revokeShareLink(projectId: string, scanId: string): Promise<void> {
    await this.request<void>(`/projects/${projectId}/scans/${scanId}/share`, { method: 'DELETE' });
  }

  // ── Trust Center (admin) ──

  async getTrustCenter(): Promise<TrustCenter> {
    return this.request<TrustCenter>('/trust-center/');
  }

  async createTrustCenter(data: {
    slug: string;
    company_name: string;
    description?: string;
    website?: string;
    contact_email?: string;
    logo_url?: string | null;
    brand_color?: string | null;
    published?: boolean;
    show_posture?: boolean;
  }): Promise<TrustCenter> {
    return this.request<TrustCenter>('/trust-center/', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTrustCenter(data: {
    slug?: string;
    company_name?: string;
    description?: string;
    website?: string;
    contact_email?: string;
    logo_url?: string | null;
    brand_color?: string | null;
    published?: boolean;
    show_posture?: boolean;
    custom_domain?: string | null;
  }): Promise<TrustCenter> {
    return this.request<TrustCenter>('/trust-center/', { method: 'PATCH', body: JSON.stringify(data) });
  }

  async createTrustControl(data: { category: string; title: string; description?: string; sort_order?: number }): Promise<TrustControl> {
    return this.request<TrustControl>('/trust-center/controls', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTrustControl(id: string, data: { category?: string; title?: string; description?: string; sort_order?: number }): Promise<TrustControl> {
    return this.request<TrustControl>(`/trust-center/controls/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteTrustControl(id: string): Promise<void> {
    await this.request<void>(`/trust-center/controls/${id}`, { method: 'DELETE' });
  }

  async createTrustSubprocessor(data: { name: string; purpose?: string; location?: string; url?: string }): Promise<TrustSubprocessor> {
    return this.request<TrustSubprocessor>('/trust-center/subprocessors', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTrustSubprocessor(id: string, data: { name?: string; purpose?: string; location?: string; url?: string }): Promise<TrustSubprocessor> {
    return this.request<TrustSubprocessor>(`/trust-center/subprocessors/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteTrustSubprocessor(id: string): Promise<void> {
    await this.request<void>(`/trust-center/subprocessors/${id}`, { method: 'DELETE' });
  }

  async createTrustDocument(data: { title: string; description?: string; url?: string; requires_nda?: boolean; sort_order?: number }): Promise<TrustDocument> {
    return this.request<TrustDocument>('/trust-center/documents', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTrustDocument(id: string, data: { title?: string; description?: string; url?: string; requires_nda?: boolean; sort_order?: number }): Promise<TrustDocument> {
    return this.request<TrustDocument>(`/trust-center/documents/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteTrustDocument(id: string): Promise<void> {
    await this.request<void>(`/trust-center/documents/${id}`, { method: 'DELETE' });
  }

  /** Attach/replace the hosted PDF of a document. Raw body PUT — not JSON. */
  async uploadTrustDocumentFile(id: string, file: File): Promise<TrustDocument> {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/trust-center/documents/${id}/file`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/pdf',
          'X-File-Name': file.name,
        },
        body: file,
      });
    } catch {
      throw new ApiError(0, t('errors.connection_error'));
    }
    if (!res.ok) {
      throw await this.errorFromResponse(res);
    }
    return res.json();
  }

  async getTrustCenterStats(): Promise<TrustCenterStats> {
    return this.request<TrustCenterStats>('/trust-center/stats');
  }

  async createTrustCertification(data: { framework: TrustCertificationFramework; label: string; status?: 'certified' | 'in_progress'; audit_firm?: string; issued_at?: string | null; expires_at?: string | null; document_url?: string; requires_nda?: boolean; sort_order?: number }): Promise<TrustCertification> {
    return this.request<TrustCertification>('/trust-center/certifications', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTrustCertification(id: string, data: { framework?: TrustCertificationFramework; label?: string; status?: 'certified' | 'in_progress'; audit_firm?: string; issued_at?: string | null; expires_at?: string | null; document_url?: string; requires_nda?: boolean; sort_order?: number }): Promise<TrustCertification> {
    return this.request<TrustCertification>(`/trust-center/certifications/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteTrustCertification(id: string): Promise<void> {
    await this.request<void>(`/trust-center/certifications/${id}`, { method: 'DELETE' });
  }

  async getTrustAccessRequests(): Promise<TrustAccessRequest[]> {
    return this.request<TrustAccessRequest[]>('/trust-center/access-requests');
  }

  async resolveTrustAccessRequest(id: string, status: 'approved' | 'denied'): Promise<TrustAccessRequest> {
    return this.request<TrustAccessRequest>(`/trust-center/access-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  // Admin
  async adminListUsers(): Promise<AdminUser[]> {
    return this.request<AdminUser[]>('/admin/users');
  }

  async adminGetUser(userId: string): Promise<AdminUserDetail> {
    return this.request<AdminUserDetail>(`/admin/users/${userId}`);
  }

  async adminUpdateRole(userId: string, role: string): Promise<void> {
    await this.request<void>(`/admin/users/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async adminGetStats(): Promise<AdminStats> {
    return this.request<AdminStats>('/admin/stats');
  }

  async adminImpersonate(userId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/admin/impersonate/${userId}`, {
      method: 'POST',
    });
  }

  async adminListFreeScans(params?: { page?: number; per_page?: number; scan_type?: string; email?: string }): Promise<FreeScanSummary[]> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.per_page) searchParams.set('per_page', String(params.per_page));
    if (params?.scan_type) searchParams.set('scan_type', params.scan_type);
    if (params?.email) searchParams.set('email', params.email);
    const qs = searchParams.toString();
    return this.request<FreeScanSummary[]>(`/admin/free-scans${qs ? '?' + qs : ''}`);
  }

  async adminGetFreeScan(id: string): Promise<FreeScanDetail> {
    return this.request<FreeScanDetail>(`/admin/free-scans/${id}`);
  }

  async adminGetFreeScanStats(): Promise<FreeScanStats> {
    return this.request<FreeScanStats>('/admin/free-scans/stats');
  }

  async downloadFindingPatch(findingId: string): Promise<void> {
    const res = await fetch(`${API_BASE}/findings/${findingId}/patch`, { credentials: 'include' });
    if (!res.ok) {
      let detail = t('errors.download_error', { status: res.status });
      try {
        const body = await res.json();
        if (body.detail) detail = body.detail;
      } catch { /* not JSON */ }
      throw new ApiError(res.status, detail);
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="(.+)"/);
    a.download = match ? match[1] : `condor-fix-${findingId.slice(0, 8)}.patch`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  async downloadReport(projectId: string, scanId: string, ai = true): Promise<void> {
    const lang = getLang();
    const theme = getResolvedTheme();
    const res = await fetch(
      `${API_BASE}/projects/${projectId}/scans/${scanId}/report?lang=${lang}&theme=${theme}&ai=${ai}`,
      { credentials: 'include' },
    );

    if (!res.ok) {
      let detail = t('errors.download_error', { status: res.status });
      try {
        const body = await res.json();
        if (body.detail) detail = body.detail;
      } catch { /* not JSON */ }
      throw new ApiError(res.status, detail);
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="(.+)"/);
    a.download = match ? match[1] : `condor-report-${scanId.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Fetch the interactive, self-contained HTML report (`format=html`: inline
   * CSS/JS, no external assets) as a Blob. The caller renders it inside the
   * sandboxed <ReportViewer> iframe or offers it as an .html download —
   * fetching (instead of pointing an iframe at the URL) keeps the auth cookie
   * flow in our code and lets the viewer show real loading/error states while
   * the API generates the report.
   */
  async fetchHtmlReport(projectId: string, scanId: string, ai = true): Promise<Blob> {
    const lang = getLang();
    const theme = getResolvedTheme();
    const res = await fetch(
      `${API_BASE}/projects/${projectId}/scans/${scanId}/report?format=html&lang=${lang}&theme=${theme}&ai=${ai}`,
      { credentials: 'include' },
    );

    if (!res.ok) {
      let detail = t('errors.download_error', { status: res.status });
      try {
        const body = await res.json();
        if (body.detail) detail = body.detail;
      } catch { /* not JSON */ }
      throw new ApiError(res.status, detail);
    }

    return res.blob();
  }

  // ── MCP management (connected AI clients + audit log) ──
  async getMcpInfo(): Promise<McpInfo> {
    return this.request<McpInfo>('/mcp/info');
  }

  async getMcpClients(): Promise<McpClient[]> {
    return this.request<McpClient[]>('/mcp/clients');
  }

  async revokeMcpClient(clientId: string): Promise<{ client_id: string; status: string }> {
    return this.request<{ client_id: string; status: string }>(
      `/mcp/clients/${encodeURIComponent(clientId)}/revoke`,
      { method: 'POST' },
    );
  }

  async getMcpAccessLog(params?: { limit?: number; offset?: number }): Promise<McpAccessLogPage> {
    const sp = new URLSearchParams();
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.offset) sp.set('offset', String(params.offset));
    const qs = sp.toString();
    return this.request<McpAccessLogPage>(`/mcp/access-log${qs ? '?' + qs : ''}`);
  }
}

export const api = new ApiClient();

/** Run a free SSL/TLS check (public, no auth required). */
export async function freeSSLCheck(url: string, email?: string): Promise<FreeCheckResult> {
  const payload: Record<string, string> = { url };
  if (email) payload.email = email;
  const res = await fetch(`${API_BASE}/free/ssl-check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = 'Scan failed';
    try {
      const body = await res.json();
      if (body.detail) detail = body.detail;
    } catch { /* not JSON */ }
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

/** Run a free CMS check (public, no auth required). */
export async function freeCMSCheck(url: string, email?: string): Promise<FreeCheckResult> {
  const payload: Record<string, string> = { url };
  if (email) payload.email = email;
  const res = await fetch(`${API_BASE}/free/cms-check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = 'Scan failed';
    try {
      const body = await res.json();
      if (body.detail) detail = body.detail;
    } catch { /* not JSON */ }
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

/** Fetch a shared report by token (public, no auth required). */
export async function fetchSharedReport(token: string): Promise<SharedReportData> {
  const res = await fetch(`${API_BASE}/reports/shared/${encodeURIComponent(token)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = 'Report not found or expired';
    try {
      const body = await res.json();
      if (body.detail) detail = body.detail;
    } catch { /* not JSON */ }
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

/** Fetch a public trust center by slug (public, no auth required). */
export async function fetchTrustCenter(slug: string): Promise<TrustCenterPublic> {
  const res = await fetch(`${API_BASE}/trust/${encodeURIComponent(slug)}`);
  if (!res.ok) {
    let detail = 'Trust center not found';
    try {
      const body = await res.json();
      if (body.detail) detail = body.detail;
    } catch { /* not JSON */ }
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

/** Fetch a public trust center by custom domain (public, no auth required). */
export async function fetchTrustCenterByDomain(host: string): Promise<TrustCenterPublic> {
  const res = await fetch(`${API_BASE}/trust/by-domain/${encodeURIComponent(host)}`);
  if (!res.ok) {
    let detail = 'Trust center not found';
    try {
      const body = await res.json();
      if (body.detail) detail = body.detail;
    } catch { /* not JSON */ }
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

/** Fetch an approved access grant (public, token-authenticated). 404 = invalid, 410 = expired. */
export async function fetchTrustAccessGrant(slug: string, token: string): Promise<TrustAccessGrant> {
  const res = await fetch(`${API_BASE}/trust/${encodeURIComponent(slug)}/access/${encodeURIComponent(token)}`);
  if (!res.ok) {
    let detail = 'Access link not valid';
    try {
      const body = await res.json();
      if (body.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch { /* not JSON */ }
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

/** Submit an access request for NDA-gated documents (public, no auth required). */
export async function submitTrustAccessRequest(
  slug: string,
  data: { email: string; company?: string; message?: string; nda_accepted: boolean },
): Promise<{ id: string; status: string }> {
  const res = await fetch(`${API_BASE}/trust/${encodeURIComponent(slug)}/access-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let detail = 'Request failed';
    try {
      const body = await res.json();
      if (body.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch { /* not JSON */ }
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

/**
 * Create an EventSource for scan progress SSE with automatic reconnection.
 * Returns a cleanup function to close the connection.
 */
export function subscribeScanEvents(
  projectId: string,
  scanId: string,
  onEvent: (event: { type: string; data: unknown }) => void,
  onDone?: () => void,
  onConnectionError?: (attempt: number) => void,
): () => void {
  let aborted = false;
  let controller = new AbortController();
  const MAX_RETRIES = 5;
  const BASE_DELAY = 1000; // 1s

  async function connect(attempt: number) {
    if (aborted) return;
    controller = new AbortController();

    try {
      const res = await fetch(
        `${API_BASE}/projects/${projectId}/scans/${scanId}/events`,
        { signal: controller.signal, credentials: 'include' },
      );

      if (!res.ok || !res.body) {
        if (!aborted && attempt < MAX_RETRIES) {
          onConnectionError?.(attempt + 1);
          const delay = BASE_DELAY * Math.pow(2, attempt);
          await new Promise(r => setTimeout(r, delay));
          return connect(attempt + 1);
        }
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const raw = line.slice(6).trim();
            try {
              const parsed = JSON.parse(raw);
              onEvent({ type: eventType || parsed.type, data: parsed });
              if (eventType === 'done' || parsed.type === 'done') {
                onDone?.();
                return;
              }
            } catch { /* skip malformed */ }
          }
        }
      }

      // Stream ended without 'done' — reconnect if not aborted
      if (!aborted && attempt < MAX_RETRIES) {
        onConnectionError?.(attempt + 1);
        const delay = BASE_DELAY * Math.pow(2, attempt);
        await new Promise(r => setTimeout(r, delay));
        connect(attempt + 1);
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      if (!aborted && attempt < MAX_RETRIES) {
        onConnectionError?.(attempt + 1);
        const delay = BASE_DELAY * Math.pow(2, attempt);
        await new Promise(r => setTimeout(r, delay));
        connect(attempt + 1);
      } else {
        console.warn('SSE connection failed after retries:', err);
      }
    }
  }

  connect(0);

  return () => {
    aborted = true;
    controller.abort();
  };
}
